import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import DataSource from 'devextreme/data/data_source';
import { Subject, Subscription, zip } from 'rxjs';
import { UserDomainService } from '../user-domains/user-domain.service';
import { Field, User } from 'shared';
import { FieldService } from '../stores/field.service';
import { LayerGroup, Map as LMap, Polygon } from 'leaflet';
import { MapService } from '../services/map.service';
import { debounceTime, filter, first } from 'rxjs/operators';
import { NgElement, WithProperties } from '@angular/elements';
import { MapFieldPopupComponent } from './map-field-popup/map-field-popup.component';
import { DxDataGridComponent, DxTextBoxComponent } from 'devextreme-angular';
import { MapSidebarFilterService } from '../map/map-sidebar-filter/map-sidebar-filter.service';
import { FieldCropsPopupComponent } from '../field-crops/field-crops-popup/field-crops-popup.component';
import { CropService } from '../stores/crop.service';

@Component({
  selector: 'app-map-fields',
  templateUrl: './map-fields.component.html',
  styleUrls: ['./map-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapFieldsComponent implements OnInit, AfterViewInit, OnDestroy {
  remoteDs: DataSource;
  canEdit: boolean;
  canAdd: boolean;
  canDelete: boolean;
  private mapGroup: LayerGroup;
  private polygons: Map<number, Polygon> = new Map<number, Polygon>();

  @ViewChild(DxDataGridComponent)
  grid: DxDataGridComponent;

  @ViewChild(DxTextBoxComponent)
  searchTxtBox: DxTextBoxComponent;

  public map: LMap;

  editMenu = [
    { value: 'crops', name: 'Cultures', icon: 'ld-crop' },
    { value: 'fit', name: 'Visiter', icon: 'map' },
  ];
  private sideFilterSub: Subscription;

  @ViewChild(FieldCropsPopupComponent, { static: true })
  fieldCropsPopup: FieldCropsPopupComponent;
  crops: Promise<any>;

  groupExpanded = true;
  groupToggleIcon: string = this.groupExpanded ? 'collapse' : 'expand';

  dsLoadChanged$ = new Subject<void>();
  contextMenuTarget: any;
  contextMenuVisible: boolean;

  constructor(
    private fieldService: FieldService,
    private userDomainsService: UserDomainService,
    private mapSidebarFilter: MapSidebarFilterService,
    public user: User,
    private cropService: CropService,
    private mapService: MapService,
  ) {

    // Permissions
    this.canEdit = user.can('edit fields');
    this.canAdd = user.can('add fields');
    this.canDelete = user.can('delete fields');

    // Create map group
    this.mapGroup = new LayerGroup<Polygon>();

    // Wait map ready
    this.mapService.mapReady$.pipe(first()).subscribe((map) => {
      this.map = map;
      this.mapGroup.addTo(map);
    });
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    // Fetch crops
    this.crops = this.cropService.newDataSource({ paginate: false }).load();

    this.remoteDs = this.fieldService.newDataSource({
      paginate: false,
      pageSize: 100000,
      reshapeOnPush: true,
      pushAggregationTimeout: 500,
      onLoadingChanged: (isLoading) => {
        if (!isLoading) {
          this.dsLoadChanged$.next();
        }
      }
    }, true);

    // Defer rendering
    this.grid.instance.option('dataSource', this.remoteDs);

    // Sidebar filter
    this.sideFilterSub = this.mapSidebarFilter.filter$.subscribe(() => {
      // Update map
      zip(
        this.dsLoadChanged$,
        this.mapService.mapReady$,
        this.grid.onOptionChanged.pipe(
          filter(o => o.name === 'columns'),
          debounceTime(200),
        )
      ).pipe(
        first()
      ).subscribe(() => {
        this.updateMap();
      });
      this.mapSidebarFilter.applyOnGrid(this.grid.instance);
      this.grid.instance.repaint();
    });
  }


  addPolygon = (f: Field) => {
    const polygon = f.createLayer({
      fillColor: f.color,
      weight: 1,
    });

    let popupEl: NgElement & WithProperties<MapFieldPopupComponent> = null;

    polygon.bindPopup(() => {
      if (popupEl === null) {
        popupEl = document.createElement('field-popup') as any;
      }
      popupEl.field = f;
      return popupEl;
    }, {
      offset: [0, -20],
      minWidth: 300,
      className: 'field-map-popup',
    });

    polygon.bindTooltip(f.name, {
      direction: 'center',
      opacity: 0.6,
    });

    this.mapGroup.addLayer(polygon);
    this.polygons.set(f.id, polygon);
  }

  private updateMap() {

    // Clear layers
    this.mapGroup.clearLayers();

    // Clear dict
    this.polygons.clear();

    this.updateMapRecursive(this.remoteDs.items());
  }

  private updateMapRecursive(list: (Field & { items })[]) {

    if (!list) {
      return;
    }

    list.forEach(f => {
      if (f.hasOwnProperty('items')) {
        this.updateMapRecursive(f.items);
      } else {
        this.addPolygon(f);
      }
    });
  }

  editMenuClick(e: any, field: Field) {
    switch (e.itemData.value) {
      case 'crops':
        this.showCropsGrid(field);
        break;
      case 'fit':
        this.visit(field.id);
        break;
    }
  }

  showCropsGrid(field: any) {
    this.fieldCropsPopup.showFieldCrops(field);
  }

  searchGrid(e: any) {
    this.dsLoadChanged$.pipe(first()).subscribe(() => this.updateMap());
    this.grid.instance.searchByText(e.value);
  }

  onToolbarPreparing(e: any) {
    const toolbarItems = e.toolbarOptions.items;

    toolbarItems.forEach((item) => {
      if (item.name === 'searchPanel') {
        item.location = 'before';
      }
    });
  }

  onGridContentReady(e: any) {
    e.element.querySelector('colgroup')?.remove();
    e.element.querySelectorAll('tbody > tr').forEach((node) => {
      const td = node.getElementsByTagName('td');
      while (td.length > 1) {
        td[0].remove();
      }
      td[0].removeAttribute('colspan');
    });
  }

  onRowClick(e: any) {
    if (e.rowType === 'group') {
      return;
    }
    this.visit(e.data.id);
  }

  visit(id: number) {
    const polygon = this.polygons.get(id);
    if (!polygon) {
      return;
    }

    this.mapService.mapReady$.pipe(first()).subscribe((map) => {

      map.fitBounds(polygon.getBounds(), {
        padding: [50, 50],
        maxZoom: 17,
      });

      polygon.openPopup();
    });

  }

  filterClick = () => {
    this.mapSidebarFilter.show();
  }

  toggleSearch = (e: any) => {
    this.searchTxtBox.instance.focus();
  }

  refreshGrid = (e: any) => {
    // Disable loading panel
    this.grid.instance.refresh().then(() => {
      this.grid.instance.repaint();
    });
  }

  groupsToggle = (e: any) => {
    if (this.groupExpanded) {
      this.grid.instance.collapseAll();
      this.groupExpanded = false;
      this.groupToggleIcon = 'expand';
    } else {
      this.grid.instance.expandAll();
      this.groupExpanded = true;
      this.groupToggleIcon = 'collapse';
    }
    this.dsLoadChanged$.pipe(first()).subscribe(() => this.updateMap());
  }

  onRowExpanded(e: any) {
    this.updateMap();
  }

  onRowCollapsed(e: any) {
    this.updateMap();
  }

  ngOnDestroy(): void {
    this.sideFilterSub.unsubscribe();
  }
}

